/**
 * The create wizard's last step — a starting finish for every wall and the
 * ceiling (calculations/plan/starterFinish.ts), shown as it will be built.
 *
 * Along the top, the room unfolded: the ceiling from above, then the four
 * walls flat, each with its real outline (a gable end is a pentagon) and the
 * product drawn on it — board seams, the SlatWall's 6" rows. A wall is its own
 * switch: click it to put the SlatWall band on it or take it off. Below:
 *   - looks: Trusscore's design presets, one click each;
 *   - which walls get SlatWall, as little floor plans (all, front & back, the
 *     sides, the back wall, none);
 *   - the board's colour and direction; the band's colour and height (a share
 *     of the wall, shown as the whole 6" rows it comes to); the ceiling, on or
 *     off, and its colour.
 * Colours are only those the retailer carries. The room editor's Finish tab
 * (RoomEditDialog) uses it too, on a room that already exists.
 */

import React from 'react';
import { ProductColor, PanelOrientation } from '../../../types';
import { DESIGN_PRESETS } from '../../../data/presets';
import { ceilingGeometry, WallFaceName } from '../../../calculations/ceiling/profile';
import {
  PlanRoom, SLATWALL_PATTERNS, SlatwallPattern, StarterFinish, slatwallBand, slatwallPatternOf,
} from '../../../calculations/plan';
import { getAvailableColors, pickColor } from '../sectionOptions';
import { formatFeetInches } from '../SectionEditOverlay';
import { usePhoneSized } from '../chrome/usePhoneSized';
import { DESIGNER_STRINGS as S } from '../strings';

const W = S.planner.wizard;
const FACES: WallFaceName[] = ['front', 'right', 'back', 'left'];
const ACCENT = '#FEB100';
const BOARD_FILL: Record<ProductColor, string> = { White: '#f5f6f7', Gray: '#D0D3D4' };
const SLAT_FILL: Record<ProductColor, string> = { White: '#fbfbfc', Gray: '#c3c7c9' };
/** Wall&CeilingBoard panels are 16" wide. */
const BOARD_FT = 16 / 12;

export interface FinishStepProps {
  room: PlanRoom;
  finish: StarterFinish;
  onChange: (finish: StarterFinish) => void;
  /** Create the room bare instead (the wizard). */
  onSkip?: () => void;
}

/** The finish a room starts with: white boards, a gray SlatWall band on every wall, a white ceiling — in what the retailer carries. */
export function startingFinish(): StarterFinish | null {
  const board = pickColor('WCB', 'White');
  if (!board) return null;
  const slat = pickColor('SlatWall', 'Gray');
  return {
    board: { color: board, orientation: 'Horizontal' },
    slatwall: { color: slat ?? board, percent: 50 },
    slatwallWalls: slat ? SLATWALL_PATTERNS.all : SLATWALL_PATTERNS.none,
    ceiling: board,
  };
}

// ---------------------------------------------------------------------------
// The room, unfolded
// ---------------------------------------------------------------------------

/** One surface's product, drawn into a box `w × h` px (y down) — boards with seams, and the band. */
const Product: React.FC<{
  x: number; y: number; w: number; h: number; s: number; color: ProductColor; orientation: PanelOrientation;
  band?: { y: number; height: number; color: ProductColor } | null; clip?: string;
}> = ({ x, y, w, h, s, color, orientation, band, clip }) => {
  const seams: React.ReactNode[] = [];
  if (orientation === 'Horizontal') {
    for (let f = BOARD_FT; f * s < h - 1; f += BOARD_FT) seams.push(<line key={`h${f}`} x1={x} x2={x + w} y1={y + h - f * s} y2={y + h - f * s} />);
  } else {
    for (let f = BOARD_FT; f * s < w - 1; f += BOARD_FT) seams.push(<line key={`v${f}`} x1={x + f * s} x2={x + f * s} y1={y} y2={y + h} />);
  }
  const rows: React.ReactNode[] = [];
  if (band) {
    for (let r = 0.5; r < band.height - 1e-6; r += 0.5) {
      const yy = y + h - (band.y + r) * s;
      rows.push(<line key={r} x1={x} x2={x + w} y1={yy} y2={yy} />);
    }
  }
  return (
    <g clipPath={clip}>
      <rect x={x} y={y} width={w} height={h} fill={BOARD_FILL[color]} />
      <g stroke="rgba(0,0,0,0.13)" strokeWidth={0.7}>{seams}</g>
      {band && (
        <>
          <rect x={x} y={y + h - (band.y + band.height) * s} width={w} height={band.height * s} fill={SLAT_FILL[band.color]} />
          <g stroke={band.color === 'Gray' ? 'rgba(0,0,0,0.32)' : 'rgba(0,0,0,0.2)'} strokeWidth={0.8}>{rows}</g>
          <rect x={x} y={y + h - (band.y + band.height) * s} width={w} height={band.height * s} fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth={0.8} />
        </>
      )}
    </g>
  );
};

const Unfolded: React.FC<{ room: PlanRoom; finish: StarterFinish; slatAvailable: boolean; onToggle: (i: number) => void; twoRows: boolean }> = ({ room, finish, slatAvailable, onToggle, twoRows }) => {
  const g = ceilingGeometry(room);
  const walls = FACES.map((face, i) => ({ face, i, width: i % 2 === 0 ? room.length : room.width, outline: g.wallOutline(face) }));
  const top = Math.max(...walls.flatMap((w) => w.outline.map((p) => p[1])));
  const band = slatwallBand(room.height, finish.slatwall.percent);
  // Fit the ceiling (from above) and the four walls side by side, a gap between
  // each: in one row, or — on a phone — two, so they are not too small to read.
  const VBW = 640;
  const GAP = 14;
  const LABEL = 20;
  type Item = { key: 'ceiling' | WallFaceName; width: number; height: number };
  const items: Item[] = [{ key: 'ceiling', width: room.length, height: room.width }, ...walls.map((w) => ({ key: w.face, width: w.width, height: top }))];
  const rows: Item[][] = twoRows ? [items.slice(0, 3), items.slice(3)] : [items];
  const s = Math.min(...rows.map((r) => (VBW - GAP * (r.length - 1)) / r.reduce((t, it) => t + it.width, 0)), ...rows.map((r) => 150 / Math.max(...r.map((it) => it.height))));
  // Where each item sits: its left edge and the baseline of its row.
  const place: Record<string, { x: number; base: number }> = {};
  let rowTop = 0;
  for (const r of rows) {
    const base = rowTop + Math.max(...r.map((it) => it.height)) * s + 4;
    let x = (VBW - (r.reduce((t, it) => t + it.width, 0) * s + GAP * (r.length - 1))) / 2;
    for (const it of r) { place[it.key] = { x, base }; x += it.width * s + GAP; }
    rowTop = base + LABEL + 6;
  }
  const VBH = rowTop - 6;

  const { x: ceilingX, base } = place.ceiling;
  const ceilW = room.length * s;
  const ceilH = room.width * s;
  const ceilY = base - ceilH;

  return (
    <svg className="cw-unfolded" viewBox={`0 0 ${VBW} ${VBH}`} role="group" aria-label={W.finishTitle}>
      {/* The ceiling, from above. */}
      <g>
        <defs>
          <pattern id="cw-bare" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <path d="M0,0 L0,6" stroke="rgba(234,242,255,0.18)" strokeWidth={2} />
          </pattern>
        </defs>
        {finish.ceiling
          ? <Product x={ceilingX} y={ceilY} w={ceilW} h={ceilH} s={s} color={finish.ceiling} orientation="Horizontal" />
          : <rect x={ceilingX} y={ceilY} width={ceilW} height={ceilH} fill="url(#cw-bare)" />}
        {g.creases.map((c, k) => (
          <line key={k} x1={ceilingX + c[0][0] * s} y1={ceilY + c[0][2] * s} x2={ceilingX + c[1][0] * s} y2={ceilY + c[1][2] * s} stroke="rgba(0,0,0,0.35)" strokeWidth={1} strokeDasharray="4 3" />
        ))}
        <rect x={ceilingX} y={ceilY} width={ceilW} height={ceilH} fill="none" stroke="#eaf2ff" strokeWidth={1.2} />
        <text x={ceilingX + ceilW / 2} y={base + 14} textAnchor="middle" className="cw-unfolded-label">{W.ceiling}{finish.ceiling ? '' : ` · ${W.bare}`}</text>
      </g>

      {walls.map((wall) => {
        const { x: wx, base } = place[wall.face];
        const w = wall.width * s;
        const h = top * s;
        const y = base - h;
        const pts = wall.outline.map(([px, py]) => `${wx + px * s},${base - py * s}`).join(' ');
        const on = slatAvailable && !!finish.slatwallWalls[wall.i];
        const clip = `cw-clip-${wall.face}`;
        const label = W.faces[wall.face];
        return (
          <g
            key={wall.face}
            className={`cw-wall${slatAvailable ? ' is-toggle' : ''}${on ? ' is-on' : ''}`}
            role={slatAvailable ? 'switch' : undefined}
            aria-checked={slatAvailable ? on : undefined}
            aria-label={slatAvailable ? W.faceToggle(label, on) : label}
            tabIndex={slatAvailable ? 0 : undefined}
            onClick={slatAvailable ? () => onToggle(wall.i) : undefined}
            onKeyDown={slatAvailable ? (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(wall.i); } } : undefined}
          >
            <title>{slatAvailable ? W.faceToggle(label, on) : label}</title>
            <defs><clipPath id={clip}><polygon points={pts} /></clipPath></defs>
            <Product
              x={wx} y={y} w={w} h={h} s={s} clip={`url(#${clip})`}
              color={finish.board.color} orientation={finish.board.orientation}
              band={on && band ? { y: band.y, height: band.height, color: finish.slatwall.color } : null}
            />
            <polygon points={pts} fill="none" stroke={on ? ACCENT : '#eaf2ff'} strokeWidth={on ? 2 : 1.2} strokeLinejoin="round" className="cw-wall-edge" />
            <text x={wx + w / 2} y={base + 14} textAnchor="middle" className="cw-unfolded-label">
              {on && '● '}{label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

/** A floor plan from above with the SlatWall walls in gold: front is the top edge, as on the blueprint. */
const PatternIcon: React.FC<{ walls: boolean[]; active: boolean }> = ({ walls, active }) => {
  const off = active ? 'rgba(26,26,30,0.45)' : 'rgba(238,241,246,0.4)';
  const on = active ? '#1a1a1e' : ACCENT;
  const edge = (i: number) => (walls[i] ? { stroke: on, strokeWidth: 3 } : { stroke: off, strokeWidth: 1.2 });
  return (
    <svg width={30} height={22} viewBox="0 0 30 22" aria-hidden="true">
      <line x1={4} y1={3} x2={26} y2={3} {...edge(0)} strokeLinecap="round" />
      <line x1={27} y1={4} x2={27} y2={18} {...edge(1)} strokeLinecap="round" />
      <line x1={4} y1={19} x2={26} y2={19} {...edge(2)} strokeLinecap="round" />
      <line x1={3} y1={4} x2={3} y2={18} {...edge(3)} strokeLinecap="round" />
    </svg>
  );
};

const Swatches: React.FC<{ colors: ProductColor[]; value: ProductColor | null; onChange: (c: ProductColor) => void; label: string; disabled?: boolean }> = ({ colors, value, onChange, label, disabled }) => (
  <div className="cw-swatches" role="radiogroup" aria-label={label}>
    {colors.map((c) => (
      <button
        key={c} type="button" role="radio" aria-checked={value === c} disabled={disabled}
        className={`cw-swatch${value === c ? ' is-active' : ''}`} onClick={() => onChange(c)} title={W.colors[c]}
      >
        <span className="cw-swatch-chip" style={{ background: BOARD_FILL[c] }} />
        {W.colors[c]}
      </button>
    ))}
  </div>
);

/** A board's direction: the seams drawn the way they run. */
const DirectionIcon: React.FC<{ orientation: PanelOrientation }> = ({ orientation }) => (
  <svg width={16} height={14} viewBox="0 0 16 14" aria-hidden="true">
    <rect x={1} y={1} width={14} height={12} rx={1.5} fill="none" stroke="currentColor" strokeWidth={1.2} />
    {orientation === 'Horizontal'
      ? <path d="M1,5 H15 M1,9 H15" stroke="currentColor" strokeWidth={1} />
      : <path d="M5.7,1 V13 M10.3,1 V13" stroke="currentColor" strokeWidth={1} />}
  </svg>
);

/** A look's swatch: a scrap of wall in its colours. */
const LookIcon: React.FC<{ board: ProductColor; slat: ProductColor | null; coverage: number }> = ({ board, slat, coverage }) => (
  <svg width={34} height={24} viewBox="0 0 34 24" aria-hidden="true">
    <rect x={0.5} y={0.5} width={33} height={23} rx={3} fill={BOARD_FILL[board]} stroke="rgba(0,0,0,0.25)" />
    {slat && coverage > 0 && (
      <g>
        <rect x={0.5} y={12 - coverage * 0.12} width={33} height={coverage * 0.24} fill={SLAT_FILL[slat]} />
        {Array.from({ length: Math.round(coverage / 12.5) + 1 }, (_, k) => (
          <line key={k} x1={1} x2={33} y1={12 - coverage * 0.12 + k * 3} y2={12 - coverage * 0.12 + k * 3} stroke="rgba(0,0,0,0.3)" strokeWidth={0.6} />
        ))}
      </g>
    )}
  </svg>
);

export const FinishStep: React.FC<FinishStepProps> = ({ room, finish, onChange, onSkip }) => {
  const phone = usePhoneSized();
  const boardColors = getAvailableColors('WCB');
  const slatColors = getAvailableColors('SlatWall');
  const slatAvailable = slatColors.length > 0;
  const pattern = slatwallPatternOf(finish.slatwallWalls);
  const anySlat = slatAvailable && finish.slatwallWalls.some(Boolean);
  const band = slatwallBand(room.height, finish.slatwall.percent);

  const set = (patch: Partial<StarterFinish>) => onChange({ ...finish, ...patch });
  const toggleWall = (i: number) => set({ slatwallWalls: finish.slatwallWalls.map((v, k) => (k === i ? !v : v)) });

  // Trusscore's looks, in what this retailer carries.
  const looks = DESIGN_PRESETS.map((look) => {
    const board = pickColor('WCB', look.wallColor) ?? finish.board.color;
    const slat = slatAvailable ? pickColor('SlatWall', look.slatWallColor) : null;
    const ceiling = look.includeCeiling ? pickColor('WCB', look.ceilingColor) : null;
    const walls = look.slatWallCoverage > 0 && slat ? (anySlat ? finish.slatwallWalls : SLATWALL_PATTERNS.all) : SLATWALL_PATTERNS.none;
    const next: StarterFinish = {
      board: { color: board, orientation: look.wallOrientation },
      slatwall: { color: slat ?? finish.slatwall.color, percent: look.slatWallCoverage > 0 ? look.slatWallCoverage : finish.slatwall.percent },
      slatwallWalls: walls,
      ceiling,
    };
    const active = finish.board.color === board && finish.board.orientation === look.wallOrientation && finish.ceiling === ceiling
      && (look.slatWallCoverage > 0
        ? anySlat && finish.slatwall.color === slat && finish.slatwall.percent === look.slatWallCoverage
        : !anySlat);
    return { look, next, active, board, slat };
  });

  return (
    <div className="cw-finish">
      <Unfolded room={room} finish={finish} slatAvailable={slatAvailable} onToggle={toggleWall} twoRows={phone} />

      <p className="cw-label">{W.looks}</p>
      <div className="cw-looks">
        {looks.map(({ look, next, active, board, slat }) => (
          <button key={look.id} type="button" className={`cw-look${active ? ' is-active' : ''}`} aria-pressed={active} title={look.description} onClick={() => onChange(next)}>
            <LookIcon board={board} slat={slat} coverage={look.slatWallCoverage} />
            <span>{look.name}</span>
          </button>
        ))}
      </div>

      {slatAvailable && (
        <>
          <p className="cw-label">{W.slatwallOn}</p>
          <div className="cw-patterns" role="radiogroup" aria-label={W.slatwallOn}>
            {(Object.keys(SLATWALL_PATTERNS) as SlatwallPattern[]).map((p) => (
              <button
                key={p} type="button" role="radio" aria-checked={pattern === p}
                className={`cw-pattern${pattern === p ? ' is-active' : ''}`}
                onClick={() => set({ slatwallWalls: SLATWALL_PATTERNS[p] })}
              >
                <PatternIcon walls={SLATWALL_PATTERNS[p]} active={pattern === p} />
                <span>{W.patterns[p]}</span>
              </button>
            ))}
            {!pattern && (
              <span className="cw-pattern is-active is-custom" aria-checked role="radio">
                <PatternIcon walls={finish.slatwallWalls} active />
                <span>{W.custom_walls}</span>
              </span>
            )}
          </div>
        </>
      )}

      <div className="cw-finish-grid">
        <section>
          <p className="cw-label">{W.board}</p>
          <Swatches colors={boardColors} value={finish.board.color} label={W.board} onChange={(c) => set({ board: { ...finish.board, color: c } })} />
          <div className="cw-seg" role="radiogroup" aria-label={W.board}>
            {(['Horizontal', 'Vertical'] as PanelOrientation[]).map((o) => (
              <button key={o} type="button" role="radio" aria-checked={finish.board.orientation === o} className={finish.board.orientation === o ? 'is-active' : ''} onClick={() => set({ board: { ...finish.board, orientation: o } })}>
                <DirectionIcon orientation={o} /> {W.direction[o]}
              </button>
            ))}
          </div>
        </section>

        {slatAvailable && (
          <section className={anySlat ? undefined : 'is-off'}>
            <p className="cw-label">{W.slatwall}</p>
            <Swatches colors={slatColors} value={finish.slatwall.color} label={W.slatwall} disabled={!anySlat} onChange={(c) => set({ slatwall: { ...finish.slatwall, color: c } })} />
            <label className="cw-slider">
              <span className="cw-slider-head">
                <span>{W.bandSize}</span>
                <strong>{finish.slatwall.percent}%</strong>
              </span>
              <input
                type="range" min={5} max={100} step={1} value={finish.slatwall.percent} disabled={!anySlat} aria-label={W.bandSize}
                onChange={(e) => set({ slatwall: { ...finish.slatwall, percent: Number(e.target.value) } })}
              />
              <span className="cw-slider-note">{band ? W.band(formatFeetInches(band.height), band.rows) : ''}</span>
            </label>
          </section>
        )}

        <section>
          <p className="cw-label">{W.ceiling}</p>
          <div className="cw-seg" role="radiogroup" aria-label={W.ceiling}>
            <button type="button" role="radio" aria-checked={!!finish.ceiling} className={finish.ceiling ? 'is-active' : ''} onClick={() => set({ ceiling: finish.ceiling ?? pickColor('WCB', finish.board.color) })}>
              {W.ceilingYes}
            </button>
            <button type="button" role="radio" aria-checked={!finish.ceiling} className={finish.ceiling ? '' : 'is-active'} onClick={() => set({ ceiling: null })}>
              {W.ceilingNo}
            </button>
          </div>
          {finish.ceiling && <Swatches colors={boardColors} value={finish.ceiling} label={W.ceiling} onChange={(c) => set({ ceiling: c })} />}
        </section>
      </div>

      {onSkip && <button type="button" className="cw-skip" onClick={onSkip}>{W.skip}</button>}
    </div>
  );
};

export default FinishStep;
